import { S3Client, PutObjectCommand, DeleteObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';

// Digital Ocean Spaces configuration
const spacesConfig = {
    endpoint: process.env.REACT_APP_DO_SPACES_ENDPOINT || 'https://nyc3.digitaloceanspaces.com',
    region: process.env.REACT_APP_DO_SPACES_REGION || 'nyc3',
    credentials: {
        accessKeyId: process.env.REACT_APP_DO_SPACES_ACCESS_KEY,
        secretAccessKey: process.env.REACT_APP_DO_SPACES_SECRET_KEY,
    },
};

const spacesClient = new S3Client(spacesConfig);

export const SPACES_BUCKET = process.env.REACT_APP_DO_SPACES_BUCKET || 'moniker-assets';

/**
 * Upload a file to Digital Ocean Spaces
 * @param {File} file - The file to upload
 * @param {string} key - The path/key where to store the file (e.g., 'locations/loc-123/image.jpg')
 * @param {object} options - Additional options like ContentType, ACL, etc.
 * @returns {Promise<string>} - The public URL of the uploaded file
 */
export const uploadToSpaces = async (file, key, options = {}) => {
    try {
        // Convert File to ArrayBuffer for better compatibility
        const fileBuffer = await file.arrayBuffer();
        
        const command = new PutObjectCommand({
            Bucket: SPACES_BUCKET,
            Key: key,
            Body: new Uint8Array(fileBuffer),
            ContentType: file.type || 'image/jpeg',
            ACL: 'public-read', // Make the file publicly accessible
            ...options
        });

        await spacesClient.send(command);
        
        // Construct the public URL
        const publicUrl = `${spacesConfig.endpoint.replace('https://', `https://${SPACES_BUCKET}.`)}/${key}`;
        
        return publicUrl;
    } catch (error) {
        console.error('Error uploading to Digital Ocean Spaces:', error);
        throw new Error(`Upload failed: ${error.message}`);
    }
};

/**
 * Delete a file from Digital Ocean Spaces
 * @param {string} key - The path/key of the file to delete
 * @returns {Promise<void>}
 */
export const deleteFromSpaces = async (key) => {
    try {
        const command = new DeleteObjectCommand({
            Bucket: SPACES_BUCKET,
            Key: key,
        });

        await spacesClient.send(command);
        console.log(`Successfully deleted: ${key}`);
    } catch (error) {
        console.error('Error deleting from Digital Ocean Spaces:', error);
        throw new Error(`Delete failed: ${error.message}`);
    }
};

/**
 * Generate a unique filename for uploads
 * @param {string} originalName - The original filename
 * @param {string} prefix - Optional prefix (e.g., 'locations/loc-123/')
 * @returns {string} - A unique filename
 */
export const generateUniqueFilename = (originalName, prefix = '') => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop();
    const baseName = originalName.split('.').slice(0, -1).join('.');
    
    // Clean the basename to be URL-safe
    const cleanBaseName = baseName.toLowerCase().replace(/[^a-z0-9]/g, '-');
    
    const filename = `${cleanBaseName}-${timestamp}-${randomStr}.${extension}`;
    
    return prefix ? `${prefix}${filename}` : filename;
};

/**
 * Upload location image with proper path structure
 * @param {File} file - The image file to upload
 * @param {string} locationId - The location ID
 * @returns {Promise<string>} - The public URL of the uploaded image
 */
export const uploadLocationImage = async (file, locationId) => {
    const key = generateUniqueFilename(file.name, `agenda/locations/${locationId}/`);
    return await uploadToSpaces(file, key, {
        ContentType: file.type,
        CacheControl: 'max-age=31536000', // Cache for 1 year
    });
};

/**
 * Delete location image by URL
 * @param {string} imageUrl - The full URL of the image to delete
 * @returns {Promise<void>}
 */
export const deleteLocationImage = async (imageUrl) => {
    try {
        // Extract the key from the URL
        const urlParts = imageUrl.split('/');
        const bucketIndex = urlParts.findIndex(part => part === SPACES_BUCKET);
        if (bucketIndex === -1) {
            throw new Error('Invalid image URL format');
        }
        
        const key = urlParts.slice(bucketIndex + 1).join('/');
        await deleteFromSpaces(key);
    } catch (error) {
        console.error('Error deleting location image:', error);
        throw error;
    }
};

/**
 * Validate Digital Ocean Spaces configuration
 * @returns {boolean} - True if properly configured
 */
export const validateSpacesConfig = () => {
    const required = [
        'REACT_APP_DO_SPACES_ACCESS_KEY',
        'REACT_APP_DO_SPACES_SECRET_KEY',
        'REACT_APP_DO_SPACES_BUCKET'
    ];
    
    const missing = required.filter(key => !process.env[key]);
    
    if (missing.length > 0) {
        console.error('Missing Digital Ocean Spaces environment variables:', missing);
        return false;
    }
    
    return true;
};